import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { AdminIcon, FilterButton, IconBtn } from '../admin-kit';
import './people-page.css';

/**
 * Kleine onderdelen voor Medewerkers (BHR-2) die de kit (nog) niet heeft: een melding die ook een
 * fout kan zijn, en een filterknop of menuknop met een menu eronder. Na BHR-8 kunnen ze naar de
 * kit als een tweede scherm ze nodig heeft.
 */

/**
 * De melding rechtsonder (`.a-toast`), voor "gelukt" en voor "mislukt".
 *
 * `useAdminToast` uit de kit kent alleen het groene vinkje. Een fout met een groen vinkje leest
 * als een succes, en de oude melding van `@up2d8/ui` staat onder het paneel (z-index 60) en is dan
 * onzichtbaar - precies wanneer ze iets te zeggen heeft. Vijf seconden, zoals de kit.
 */
export function useMelding(duration = 5000) {
  const [melding, setMelding] = useState<{ text: ReactNode; fout: boolean } | null>(null);
  const timer = useRef<number | null>(null);

  const toon = useCallback(
    (text: ReactNode, fout: boolean) => {
      if (timer.current) window.clearTimeout(timer.current);
      setMelding({ text, fout });
      timer.current = window.setTimeout(() => setMelding(null), duration);
    },
    [duration],
  );

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const element = melding ? (
    <div
      className={melding.fout ? 'a-toast danger' : 'a-toast'}
      role={melding.fout ? 'alert' : 'status'}
      data-testid="admin-toast"
    >
      <AdminIcon name={melding.fout ? 'alert' : 'checkCircle'} size={18} />
      <div>{melding.text}</div>
    </div>
  ) : null;

  return {
    ok: useCallback((text: ReactNode) => toon(text, false), [toon]),
    fout: useCallback((text: ReactNode) => toon(text, true), [toon]),
    element,
  };
}

export interface MenuOption<V extends string> {
  value: V;
  label: ReactNode;
}

/**
 * Een menu onder een knop: open met een klik, dicht met Escape, een klik ernaast of een keuze.
 * Pijltjes lopen door de items; de focus gaat bij het sluiten terug naar de knop.
 */
function useMenu() {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const knop = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const buiten = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', buiten);
    // Het eerste (of het gekozen) item krijgt de focus, zodat het toetsenbord meteen verder kan.
    const items = wrap.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]');
    const gekozen = wrap.current?.querySelector<HTMLElement>('[aria-checked="true"]');
    (gekozen ?? items?.[0])?.focus();
    return () => document.removeEventListener('mousedown', buiten);
  }, [open]);

  const sluit = (terug = true) => {
    setOpen(false);
    if (terug) knop.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (event.key === 'Escape') {
      // Niet ook het paneel of venster eromheen sluiten.
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      sluit();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = Array.from(
      wrap.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [],
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    const volgende = event.key === 'ArrowDown' ? index + 1 : index - 1;
    items[(volgende + items.length) % items.length]?.focus();
  };

  return { open, setOpen, wrap, knop, sluit, onKeyDown };
}

/**
 * Een filter als pill (`.a-filter`) met een keuzemenu: "Status: **In dienst**". De eerste optie is
 * "alles" (waarde `''`); staat die gekozen, dan is de pill grijs en staat er alleen het label.
 */
export function FilterMenu<V extends string>({
  label,
  options,
  value,
  onChange,
  testId,
}: {
  label: string;
  options: MenuOption<V | ''>[];
  value: V | '';
  onChange: (value: V | '') => void;
  testId?: string;
}) {
  const menu = useMenu();
  const id = useId();
  const gekozen = options.find((option) => option.value === value);

  return (
    <div className="a-popwrap" ref={menu.wrap} onKeyDown={menu.onKeyDown}>
      <FilterButton
        label={label}
        value={value !== '' ? gekozen?.label : undefined}
        active={value !== ''}
        expanded={menu.open}
        aria-haspopup="menu"
        aria-controls={menu.open ? id : undefined}
        testId={testId}
        onClick={(event) => {
          menu.knop.current = event.currentTarget;
          menu.setOpen(!menu.open);
        }}
      />
      {menu.open ? (
        <div className="a-menu" role="menu" id={id} aria-label={label}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={option.value === value}
              className="a-menu-item"
              onClick={() => {
                onChange(option.value);
                menu.sluit();
              }}
            >
              {option.label}
              {option.value === value ? (
                <span className="a-menu-right">
                  <AdminIcon name="check" size={14} />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * De menuknop (drie puntjes) aan het eind van een rij, met een paar handelingen eronder.
 *
 * Het menu staat `position: fixed` onder de knop en niet `absolute`: `.a-table-wrap` scrolt
 * horizontaal, en een absoluut menu in de laatste rij zou daarin afgeknipt worden. Scrollen of
 * het venster vergroten sluit het, want dan staat het niet meer onder zijn knop.
 */
export function RowMenu({
  label,
  items,
}: {
  label: string;
  items: { key: string; label: ReactNode; onSelect: () => void }[];
}) {
  const menu = useMenu();
  const id = useId();
  const [plek, setPlek] = useState<{ top: number; right: number } | null>(null);
  const { open, setOpen } = menu;

  useEffect(() => {
    if (!open) return;
    const sluit = () => setOpen(false);
    window.addEventListener('scroll', sluit, true);
    window.addEventListener('resize', sluit);
    return () => {
      window.removeEventListener('scroll', sluit, true);
      window.removeEventListener('resize', sluit);
    };
  }, [open, setOpen]);

  return (
    <div className="a-popwrap end" ref={menu.wrap} onKeyDown={menu.onKeyDown}>
      <IconBtn
        label={label}
        icon={<AdminIcon name="more" />}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-controls={menu.open ? id : undefined}
        onClick={(event) => {
          menu.knop.current = event.currentTarget;
          const rect = event.currentTarget.getBoundingClientRect();
          setPlek({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
          menu.setOpen(!menu.open);
        }}
      />
      {menu.open && plek ? (
        <div
          className="a-menu"
          role="menu"
          id={id}
          aria-label={label}
          style={{ position: 'fixed', top: plek.top, right: plek.right, left: 'auto' }}
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className="a-menu-item"
              onClick={() => {
                // Niet terug naar de knop: wat gekozen is, opent een paneel met zijn eigen focus.
                menu.sluit(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
